import React from 'react';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../../lib/utils';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '../../../components/ui/dialog';

import { formatOtrDate } from '../utils/formatOtrDate';

export const ExpenseRecordsModal = ({
  open,
  onOpenChange,
  headName,
  records = [],
  isLoading = false,
  currencySymbol = '₹',
  onViewDocument,
  canDelete = false,
  onDelete,
  deletingLineId = null,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]',
            'w-[95vw] sm:w-[90vw] md:w-[560px] max-w-[560px]',
            'max-h-[90vh] overflow-hidden flex flex-col',
            'bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] border border-white/60',
            'duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'font-sans'
          )}
        >
          <div className="flex items-start justify-between px-4 sm:px-6 pt-6 pb-1 shrink-0">
            <div>
              <DialogTitle className="text-[20px] font-bold text-foreground tracking-tight">Expense records</DialogTitle>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">Records for the selected expense head.</p>
            </div>
            <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl text-[var(--color-text-secondary)] hover:bg-[var(--color-workspace)]/60 hover:text-[var(--color-primary)] transition shrink-0">
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </div>

          <div className="px-4 sm:px-6 py-4 overflow-y-auto flex-1">
            <h3 className="text-sm font-bold text-foreground mb-3">{headName}</h3>

            {isLoading ? (
              <div className="py-10 text-center text-sm text-[var(--color-text-secondary)]">Loading records...</div>
            ) : records.length === 0 ? (
              <div className="py-10 text-center text-sm text-[var(--color-text-secondary)] italic">
                No records added yet for this expense head.
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-[var(--color-border)]">
                <table className="max-md:block w-full text-left border-collapse">
                  <thead className="max-md:hidden">
                    <tr className="bg-[var(--color-workspace)]/50 border-b border-[var(--color-border)]">
                      <th className="px-4 py-3 text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-right">Amount</th>
                      <th className="px-4 py-3 text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">Proof</th>
                      {canDelete && (
                        <th className="px-4 py-3 text-[10px] font-bold text-[var(--color-text-secondary)] uppercase tracking-wider text-center">Action</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="max-md:block divide-y divide-[var(--color-border)]">
                    {records.map((record, idx) => (
                      <tr key={record.expenseLineId ?? record.id ?? idx} className="max-md:block max-md:px-4 max-md:py-3">
                        <td data-label="Date" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-4 py-3 text-sm font-medium text-foreground">{formatOtrDate(record.entryDate)}</td>
                        <td data-label="Amount" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-4 py-3 text-sm font-bold text-right text-foreground">
                          {currencySymbol} {Number(record.amount || 0).toLocaleString()}
                        </td>
                        <td data-label="Proof" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-4 py-3 text-center">
                          {record.hasDocument ? (
                            <button
                              type="button"
                              onClick={() => onViewDocument?.(record.expenseLineId ?? record.expenseId)}
                              className="text-[11px] font-bold text-[var(--color-primary)] hover:underline"
                            >
                              View
                            </button>
                          ) : (
                            <span className="text-[11px] text-[var(--color-text-secondary)]">
                              —
                            </span>
                          )}

                        </td>
                        {canDelete && (
                          <td data-label="Action" className="max-md:flex max-md:items-center max-md:justify-between max-md:gap-3 max-md:px-0 max-md:py-1.5 max-md:text-right max-md:before:content-[attr(data-label)] max-md:before:text-[10px] max-md:before:font-bold max-md:before:uppercase max-md:before:tracking-wider max-md:before:text-left max-md:before:text-[var(--color-text-secondary)] max-md:before:shrink-0 px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => onDelete?.(record)}
                              disabled={deletingLineId != null}
                              className="text-[11px] font-bold text-[var(--color-error)] hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deletingLineId === (record.expenseLineId ?? record.expenseId) ? 'Deleting...' : 'Delete'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--color-text-border)]/70 shrink-0">
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="h-11 px-6 rounded-xl border border-[var(--color-text-border)] text-sm font-bold text-foreground hover:bg-[var(--color-workspace)]/50 transition"
              >
                Cancel
              </button>
            </DialogPrimitive.Close>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="h-11 px-6 rounded-xl text-white text-sm font-bold transition bg-[var(--color-primary)] hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]"
              >
                Close
              </button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};